<template>
  <div class="product-selection-item product-selection-item-quote">
    <div class="product-selection-header">
      <h3 class="product-selection-header-title">Quote</h3>
      <p class="product-selection-header-text">Select a date to add to a quote.</p>
    </div>
    <div class="product-offer">
      <div class="product" ref="calendarAnchor">
        <div class="product-label">
          <span>Quote</span>
          <span v-if="selectedDateDisplay" class="product-date">
            {{ selectedDateDisplay }}
          </span>
        </div>
        <div
          ref="calendarTrigger"
          class="product-action"
          role="button"
          tabindex="0"
          aria-label="Select quote date"
          @click.stop="toggleCalendar"
          @keydown.enter.prevent.stop="toggleCalendar"
          @keydown.space.prevent.stop="toggleCalendar"
        >
          <svg class="icon-sprite" aria-hidden="true" width="14" height="14">
            <use href="#icon-calendar-solid" />
          </svg>
        </div>
      </div>

      <Teleport to="body">
        <div
          v-if="calendarOpen"
          ref="calendarPanel"
          class="date-popover"
          @click.stop
          @mousedown.stop
        >
          <div ref="arrow" class="popover-arrow"></div>
          <div class="date-picker-panel">
            <v-date-picker
              :key="`quote-${selectedDate}`"
              v-model="dateModel"
              color="green"
              trim-weeks
              :first-day-of-week="1"
              :masks="{ input: 'MM-DD-YYYY', data: 'YYYY-MM-DD' }"
              :available-dates="pickerAvailableDates"
              :min-date="minDate"
              :max-date="maxDate"
              :disabled-dates="disabledDates"
              @update:modelValue="onCalendarDateSelect"
              @dayclick="(_, event) => event.target.blur()"
            />
          </div>
        </div>
      </Teleport>

      <div class="product-offer-content">
        <template v-if="productDetails.status && availability">
          <div v-if="!productDetails.unit_of_measure_id" class="product-unit">
            <!-- <label class="product-unit-label">Unit Type</label> -->
            <select
              class="form-select"
              v-model.number="selectedUnitId"
            >
              <option
                v-for="(uom, index) in quoteUomList"
                :key="index"
                :value="uom.id"
              >
                {{ uom.name }}
              </option>
            </select>
          </div>
          <div v-else-if="categoryQuoteUomName" class="product-unit">
            <label class="product-unit-label">Unit Type</label>
            <span>{{ categoryQuoteUomName }}</span>
          </div>
        </template>
        <AddToQuote
          v-if="(productDetails.status && availability) || !isLoggedIn"
          :show-quantity="!!(productDetails.status && availability)"
          :quant="quoteQuantity"
          :prods="[quoteProduct]"
          :quote_date="selectedDate || null"
          :comingSoon="productDetails.coming_soon"
          :max-quote-quantity="quoteMaxQuantity"
          :uom-list="quoteUomList"
          :product-details-page="true"
          @update:quant="quoteQuantity = $event"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import 'v-calendar/dist/style.css';
import { DatePicker as VDatePicker } from 'v-calendar';
import { createPopper } from '@popperjs/core';
import axios from 'axios';
import AddToQuote from './AddToQuote';
import { useGetters, useMisc, useState } from '@/composables';
import { getQuoteLineItemForProduct } from '@/composables/quoteQuantitySync';
import { getShopDefaultQuantity } from '@/composables/shopQuantity';

defineOptions({ name: 'QuoteProductDetails' });

const props = defineProps({
  productDetails: {
    type: Object,
    required: true,
  },
  availability: {
    type: [Object, Array, Boolean, null],
    default: null,
  },
});

const { isLoggedIn } = useGetters();
const { quote } = useState();
const { appendTimeForBrowser, formatDateWithZeroFromStr } = useMisc();
const route = useRoute();
const router = useRouter();

const quoteQuantity = ref(1);
const selectedUnitId = ref(null);
const quoteUomList = ref(
  JSON.parse(localStorage.getItem('fl_units_of_measure') || '[]') || []
);
const quoteMaxQuantity = ref(
  JSON.parse(localStorage.getItem('site_settings') || '{}')?.max_quote_quantity
);

const selectedDate = ref('');
const dateModel = ref(null);
const calendarOpen = ref(false);
const calendarTrigger = ref(null);
const calendarAnchor = ref(null);
const calendarPanel = ref(null);
const arrow = ref(null);
let popperInstance = null;

const parseJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null || raw === '') return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const mapPickerAvailableDates = (availableDates = []) =>
  (availableDates || [])
    .map((row) => {
      if (!row) return '';
      if (typeof row === 'string') return row.split(' ')[0];
      return String(row.delivery_date || '').split(' ')[0];
    })
    .filter(Boolean);

const mapDisabledDates = (dates = []) =>
  (dates || []).map((disabledDate) => {
    const formatted = appendTimeForBrowser(disabledDate);
    return { start: formatted, end: formatted, date: disabledDate };
  });

const pickerAvailableDates = ref(
  mapPickerAvailableDates(parseJson('fl_available_dates', []))
);
const minDate = ref(
  localStorage.getItem('fl_min_date')
    ? appendTimeForBrowser(localStorage.getItem('fl_min_date'))
    : ''
);
const maxDate = ref(
  localStorage.getItem('fl_max_date')
    ? appendTimeForBrowser(localStorage.getItem('fl_max_date'))
    : ''
);
const disabledDates = ref(
  mapDisabledDates(parseJson('fl_disabled_dates', []))
);

const toLocalDateModel = (dateStr) => {
  if (!dateStr) return null;
  const normalized = String(dateStr).split(' ')[0];
  const parts = normalized.split('-');
  if (parts.length !== 3) return null;
  const [yyyy, mm, dd] = parts.map((part) => parseInt(part, 10));
  if (!yyyy || !mm || !dd) return null;
  return new Date(yyyy, mm - 1, dd);
};

const formatDisplayDate = (deliveryDate) => {
  if (!deliveryDate) return '';
  if (typeof deliveryDate === 'object') {
    const nested =
      deliveryDate.delivery_date
      || deliveryDate.date
      || '';
    return formatDisplayDate(nested);
  }
  const parts = String(deliveryDate).split(' ')[0].split('-');
  if (parts.length === 3) {
    const [yyyy, mm, dd] = parts;
    return `${mm}/${dd}/${yyyy}`;
  }
  return String(deliveryDate);
};

const selectedDateDisplay = computed(() => formatDisplayDate(selectedDate.value));

const normalizeQuoteDate = (value) => {
  if (!value) return '';
  return String(value).trim().split('T')[0].split(' ')[0];
};

const isAvailableQuoteDate = (date) => {
  if (!date) return false;
  if (!pickerAvailableDates.value.length) return true;
  return pickerAvailableDates.value.includes(date);
};

const syncQuoteDateQuery = async (date) => {
  const nextDate = normalizeQuoteDate(date);
  if (!nextDate) return;
  if (normalizeQuoteDate(route.query.quote_date) === nextDate) return;

  await nextTick();
  const nextQuery = { ...route.query, quote_date: nextDate };

  try {
    await router.replace({ query: nextQuery, hash: route.hash });
  } catch {
    // Keep the selected date even if the router update fails.
  }
};

const applyQuoteDate = (date, { syncQuery = false, requireAvailable = true } = {}) => {
  const nextDate = normalizeQuoteDate(date);
  if (!nextDate) return false;
  if (requireAvailable && !isAvailableQuoteDate(nextDate)) return false;

  selectedDate.value = nextDate;
  dateModel.value = toLocalDateModel(nextDate);
  if (syncQuery) {
    syncQuoteDateQuery(nextDate);
  }
  return true;
};

const applyDatesFromApi = (dates) => {
  if (!dates) return;
  if (Array.isArray(dates.available_dates)) {
    pickerAvailableDates.value = mapPickerAvailableDates(dates.available_dates);
  }
  disabledDates.value = mapDisabledDates(dates.disabled_dates || []);
  if (dates.min_date) {
    minDate.value = appendTimeForBrowser(dates.min_date);
  }
  if (dates.max_date) {
    maxDate.value = appendTimeForBrowser(dates.max_date);
  }
};

const fetchQuoteDateFromApi = async () => {
  const slug = props.productDetails?.slug;
  if (!slug || !isLoggedIn.value) return;

  try {
    const { data } = await axios.post(
      `${process.env.VUE_APP_API_BASE}auth/getFlowerLibraryDatesAndItems`,
      {
        product_slug: slug,
        parameters: {
          items_per_page: 1,
          page_number: 1,
          sort_by: 'Name-ASC',
          search: '',
          is_search: 0,
          is_entire: 0,
          delivery_date: normalizeQuoteDate(route.query.quote_date) || '',
        },
      },
      {
        headers: {
          'Content-type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      },
    );

    if (data?.error) return;

    applyDatesFromApi(data.dates);

    const fromQuery = normalizeQuoteDate(route.query.quote_date);
    const fromApi = normalizeQuoteDate(data?.quote_date || '');
    applyQuoteDate(fromQuery || fromApi, {
      requireAvailable: false,
      syncQuery: true,
    });
  } catch {
    applyQuoteDate(route.query.quote_date, {
      requireAvailable: false,
      syncQuery: true,
    });
  }
};

const categoryQuoteUomName = computed(() => {
  const uomId = props.productDetails.category?.unit_of_measure_id;
  if (!uomId) return '';
  return quoteUomList.value.find((uom) => uom.id === uomId)?.name || '';
});

const quoteProduct = computed(() => {
  const selectedUom = quoteUomList.value.find((uom) => uom.id === selectedUnitId.value);
  const categoryUom = quoteUomList.value.find(
    (uom) => uom.id === props.productDetails.category?.unit_of_measure_id
  );

  return {
  product_id: props.productDetails.id,
  qty: quoteQuantity.value,
  unit_of_measure_id:
    props.productDetails.unit_of_measure_id || selectedUnitId.value || null,
  unit_of_measure_name:
    categoryQuoteUomName.value
    || selectedUom?.name
    || null,
  unit_of_measure_plural:
    categoryUom?.plural
    || selectedUom?.plural
    || categoryQuoteUomName.value
    || selectedUom?.name
    || null,
  unit_id_dd: props.productDetails.unit_of_measure_id
    ? props.productDetails.unit_id_dd
    : selectedUnitId.value,
  image: props.productDetails.images?.[0],
  name: props.productDetails.name,
  slug: props.productDetails.slug,
  min_quantity: props.productDetails.quote_minimum_quantity,
  increment_quantity: props.productDetails.quote_increment_quantity,
  product_type_id: props.productDetails.product_type_id,
  category_name: props.productDetails.category?.category || props.productDetails.category_name || '',
  color_name: props.productDetails.color?.color || props.productDetails.color_name || '',
  variety_name: props.productDetails.variety?.variety || props.productDetails.variety_name || '',
  grade_name: props.productDetails.grade?.grade || props.productDetails.grade_name || '',
  };
});

const syncQuoteQuantities = () => {
  const details = props.productDetails;
  if (!details?.id) return;

  const quoteEntry = getQuoteLineItemForProduct(quote.value, { product_id: details.id });
  if (quoteEntry?.quantity != null) {
    quoteQuantity.value = getShopDefaultQuantity(
      details.quote_minimum_quantity,
      details.quote_increment_quantity,
      Number(quoteEntry.quantity) || 0,
    );
    if (quoteEntry.unit_of_measure_id) {
      selectedUnitId.value = quoteEntry.unit_of_measure_id;
    }
    return;
  }

  if (details.quote_minimum_quantity != null) {
    quoteQuantity.value = details.quote_minimum_quantity;
  } else if (details.quote_increment_quantity != null) {
    quoteQuantity.value = details.quote_increment_quantity;
  }
  if (details.unit_id_dd != null) {
    selectedUnitId.value = details.unit_id_dd;
  } else if (!selectedUnitId.value && quoteUomList.value[0]?.id) {
    selectedUnitId.value = quoteUomList.value[0].id;
  }
};

watch(
  () => [
    props.productDetails?.id,
    props.productDetails?.quote_minimum_quantity,
    props.productDetails?.quote_increment_quantity,
    props.productDetails?.unit_id_dd,
  ],
  () => {
    syncQuoteQuantities();
  },
  { immediate: true }
);

watch(
  quote,
  () => {
    syncQuoteQuantities();
  },
  { deep: true },
);

const destroyPopper = () => {
  if (popperInstance) {
    popperInstance.destroy();
    popperInstance = null;
  }
};

const closeCalendar = () => {
  destroyPopper();
  calendarOpen.value = false;
};

const initializePopper = () => {
  const anchor = calendarAnchor.value || calendarTrigger.value;
  if (!anchor || !calendarPanel.value) return;

  destroyPopper();
  popperInstance = createPopper(anchor, calendarPanel.value, {
    placement: 'bottom',
    strategy: 'fixed',
    modifiers: [
      { name: 'arrow', options: { element: arrow.value } },
      { name: 'offset', options: { offset: [0, 8] } },
      { name: 'flip', options: { fallbackPlacements: ['top'] } },
    ],
  });
};

const toggleCalendar = async () => {
  if (calendarOpen.value) {
    closeCalendar();
    return;
  }

  calendarOpen.value = true;
  await nextTick();
  initializePopper();
};

const onCalendarDateSelect = (dateValue) => {
  if (!dateValue) return;

  let nextDate = '';
  try {
    nextDate =
      formatDateWithZeroFromStr(appendTimeForBrowser(dateValue))
      || String(dateValue).split(' ')[0];
  } catch {
    nextDate = String(dateValue).split(' ')[0];
  }
  nextDate = String(nextDate || '').split(' ')[0];
  if (!nextDate) return;

  applyQuoteDate(nextDate, { syncQuery: true });
  closeCalendar();
};

const handleDocumentClick = (event) => {
  if (!calendarOpen.value) return;
  if (
    calendarPanel.value?.contains(event.target)
    || calendarTrigger.value?.contains(event.target)
  ) {
    return;
  }
  closeCalendar();
};

onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
});

watch(
  () => props.productDetails?.slug,
  (slug) => {
    if (!slug) return;
    applyQuoteDate(route.query.quote_date, { requireAvailable: false, syncQuery: true });
    fetchQuoteDateFromApi();
  },
  { immediate: true },
);

watch(
  () => route.query.quote_date,
  (quoteDate) => {
    const fromQuery = normalizeQuoteDate(quoteDate);
    if (fromQuery && fromQuery !== selectedDate.value) {
      applyQuoteDate(fromQuery, { requireAvailable: false });
      return;
    }
    if (!fromQuery && selectedDate.value) {
      syncQuoteDateQuery(selectedDate.value);
    }
  },
);

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick);
  destroyPopper();
});
</script>

<style scoped>
.product-selection-item-quote {
  padding: var(--padding-20);
  border-radius: var(--br-8);
  border: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: var(--gap-20);
  align-self: stretch;
  position: relative;
}

.product-selection-header-title {
  color: var(--body-color);
  font-size: var(--font-size-20);
  font-weight: 500;
  line-height: 1;
  margin-bottom: 0;
}

.product-selection-header-text {
  color: var(--body-color);
  font-size: var(--font-size-14);
  font-style: italic;
  line-height: 18px;
  margin-top: var(--gap-5);
  margin-bottom: 0;
}

.product-offer {
  border-radius: 12px;
  border: 1px solid var(--body-color);
  background: var(--tertiary-color);
  position: relative;
}

.product {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
  font-size: var(--font-size-16);
  padding: var(--padding-20) var(--padding-20) 0;
}

.product-label {
  flex: 1 0 0;
  color: var(--body-color);
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.product-date {
  font-size: var(--font-size-12, 12px);
  font-style: italic;
  color: var(--text-secondary, #606060);
}

.product-action {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  font-size: var(--font-size-14);
  cursor: pointer;
}

.date-popover {
  position: absolute;
  z-index: 1000;
  border-radius: var(--br-12, 12px);
}

.date-picker-panel {
  background: #fff;
  border: 1px solid var(--vc-border, #cbd5e1);
  border-radius: var(--br-12, 12px);
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.date-picker-panel :deep(.vc-container) {
  border: 0;
  box-shadow: none;
}

.popover-arrow {
  position: absolute;
  width: 10px;
  height: 10px;
  background: #fff;
  border: 1px solid;
  border-color: var(--vc-border, #cbd5e1);
  z-index: 1;
  transform-origin: center;
}

.date-popover[data-popper-placement^="bottom"] .popover-arrow {
  top: 0;
  left: 0 !important;
  border-width: 1px 0 0 1px;
  transform: translate(24px, -50%) rotate(40deg) skewX(-10deg) !important;
}

.date-popover[data-popper-placement^="top"] .popover-arrow {
  bottom: 0;
  left: 0 !important;
  border-width: 0 1px 1px 0;
  transform: translate(24px, 50%) rotate(40deg) skewX(-10deg) !important;
}

.date-popover :deep(.vc-day-content) {
  font-weight: 400;
}

.date-popover :deep(.vc-nav) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
}

.date-popover :deep(.vc-nav .vc-title) {
  font-weight: 700;
  font-size: 16px;
  color: #111827;
}

.date-popover :deep(.vc-nav button) {
  background: transparent;
  border: none;
  color: #374151;
}

.date-popover :deep(.vc-grid) {
  padding: 6px 10px 12px;
}

.date-popover :deep(.vc-weekdays) {
  color: #6b7280;
  font-weight: 600;
}

.date-popover :deep(.vc-day .vc-day-content) {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.date-popover :deep(.vc-day.is-disabled .vc-day-content) {
  color: #cbd5e1;
}

.date-popover :deep(.vc-day.is-selected .vc-day-content) {
  background: #f0fff4;
  color: #065f46;
}

.product-offer-content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--gap-10);
  justify-content: stretch;
  padding: var(--padding-20) var(--padding-20);
}

.product-unit {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
  flex-grow: 1;
  width: 100%;
}

.product-unit-label {
  color: var(--secondary-color, #6c757d);
  font-size: var(--font-size-sm, 14px);
  font-weight: 500;
  margin-bottom: 0;
}

.product-offer-content :deep(.add-to-quote-tooltip.tooltip-wrap) {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
}

.product-offer-content :deep(.action-button) {
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  width: auto;
  padding-left: 0;
  padding-right: 0;
}

.product-offer-content :deep(.quote-quantity-wrapper) {
  align-self: flex-end;
}
</style>
